import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { setOwnerBots } from '@redux/slices/appOwnerBotsSlice';

import Switch from '@mui/material/Switch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';

import Spinner from '@util/spinner';
import { safeLocalStorage } from '@utils/localstorage';
import { getOwnerBots } from '@services/owner';
import { STORAGE_OPEN_AI_API_KEY } from '@constants/aiController';

const maskToken = (token) => {
	if (!token) return '';
	const visibleChars = 4;
	const maskedSection = '***************';
	return token.slice(0, visibleChars) + maskedSection;
};

const Settings = () => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const [checkedStates, setCheckedStates] = useState({});
	const { sdk_access_token: sdkAccessToken } = useSelector((state) => state?.setUserDetails?.user);
	const ownerBotsList = useSelector((state) => state.appOwnerBots.bots);
	const [openAiApiKey, setOpenAiApiKey] = useState(safeLocalStorage.getItem(STORAGE_OPEN_AI_API_KEY));
	const [tempOpenAiApiKey, setTempOpenAiApiKey] = useState(openAiApiKey);
	const [isEditing, setIsEditing] = useState(false);
	const userInfoFromBackend = useSelector((state) => state.appUser.backendInfo);

	useEffect(() => {
		setIsLoading(false);
	}, [sdkAccessToken]);

	useEffect(() => {
		const fetchAndSetData = async () => {
			const response = await getOwnerBots();
			dispatch(setOwnerBots(response.bots));
		};

		fetchAndSetData();

		const interval = setInterval(fetchAndSetData, 1000 * 60 * 0.5);

		return () => clearInterval(interval);
	}, [dispatch]);

	const copyToClipboard = ({ value, text }) => {
		navigator.clipboard.writeText(value).then(
			() => {
				toast.success(`${text} copied to clipboard`, { position: 'top-right' });
			},
			(err) => {
				toast.success(`Could not copy ${text}`, { position: 'top-right' });
			}
		);
	};

	const handleChange = (event, deviceRefId) => {
		setCheckedStates((prevStates) => ({
			...prevStates,
			[deviceRefId]: event.target.checked,
		}));
	};

	const handleEditClick = () => {
		setTempOpenAiApiKey(openAiApiKey);
		setIsEditing(true);
	};

	const handleSaveClick = () => {
		safeLocalStorage.setItem(STORAGE_OPEN_AI_API_KEY, tempOpenAiApiKey?.replace(/^"|"$/g, ''));
		setOpenAiApiKey(tempOpenAiApiKey);
		setIsEditing(false);
	};

	return (
		<section className="w-full flex flex-col items-start gap-[22px]">
			<p className='text-[#000] text-[24px] sm:text-[42px] not-italic font-semibold leading-[42px] tracking-[-0.5px]'>Settings</p>
			<Spinner
				loading={isLoading}
				text="loading..."
			/>
			{!isLoading && sdkAccessToken &&
				<div className="flex items-center justify-between w-full flex-wrap gap-[12px] bg-white p-10 rounded-lg shadow-lg">
					<p className="text-md font-bold">SDK ACCESS TOKEN</p>
					<div className="flex items-center gap-10">
						<span className="text-gray-700 p-2 rounded-l-lg">{maskToken(sdkAccessToken)}</span>
						<button
							onClick={() => copyToClipboard({ value: sdkAccessToken, text: 'SDK access token' })}
							className="flex pl-[8px] pr-[12px] py-[6px] gap-2 rounded-[1000px] border-[1px] border-solid border-[#25BD66] bg-[#008735] text-[#F3F3F3] text-[16px] not-italic font-semibold"
						>
							<ContentCopyIcon fontSize="small" />
							Copy
						</button>
					</div>
				</div>
			}
			<div className="flex items-center justify-between w-full bg-white p-10 rounded-lg shadow-lg">
				<p className="text-md font-bold">OPEN AI API KEY</p>
				<div className="flex items-center gap-10">
					{isEditing ? (
						<input
							type="text"
							className="border border-gray-300 rounded p-2 w-[150px] sm:w-[300px] md:w-[500px] focus:border-[#25BD66] focus:outline-none"
							value={tempOpenAiApiKey}
							onChange={(e) => setTempOpenAiApiKey(e.target.value)}
						/>
					) : (
						<span className="text-gray-700 p-2 rounded-l-lg">
							{openAiApiKey ? maskToken(openAiApiKey) : "You don't have an open ai api key"}
						</span>
					)}
					{isEditing ? (
						<>
							<button
								onClick={() => setIsEditing(false)}
								className="pl-[8px] pr-[12px] py-[6px] rounded-[1000px] border-[1px] border-solid border-black bg-black text-[#F3F3F3] text-[16px] not-italic font-semibold"
							>
								Cancel
							</button>
							<button
								onClick={handleSaveClick}
								className="pl-[8px] pr-[12px] py-[6px] rounded-[1000px] border-[1px] border-solid border-[#25BD66] bg-[#008735] text-[#F3F3F3] text-[16px] not-italic font-semibold"
							>
								Save
							</button>
						</>
					) : (
						<button
							onClick={handleEditClick}
							className="flex gap-2 pl-[8px] pr-[12px] py-[6px] rounded-[1000px] border-[1px] border-solid border-black bg-black text-[#F3F3F3] text-[16px] not-italic font-semibold"
						>
							<EditIcon fontSize="small" />
							Edit
						</button>
					)}
					{openAiApiKey && !isEditing && (
						<button
							onClick={() => copyToClipboard({ value: openAiApiKey, text: 'Open AI api key' })}
							className="flex pl-[8px] pr-[12px] py-[6px] gap-2 rounded-[1000px] border-[1px] border-solid border-[#25BD66] bg-[#008735] text-[#F3F3F3] text-[16px] not-italic font-semibold"
						>
							<ContentCopyIcon fontSize="small" />
							Copy
						</button>
					)}
				</div>
			</div>
			{userInfoFromBackend?.base_wallet_address &&
				<div className="flex items-center justify-between w-full flex-wrap gap-[12px] bg-white p-10 rounded-lg shadow-lg">
					<p className="text-md font-bold">In-game wallet</p>
					<div className="flex items-center gap-10">
						<span className="text-gray-700 p-2 rounded-l-lg">{userInfoFromBackend?.base_wallet_address}</span>
						<button
							onClick={() => copyToClipboard({ value: userInfoFromBackend?.base_wallet_address, text: 'In-game wallet' })}
							className="flex pl-[8px] pr-[12px] py-[6px] gap-2 rounded-[1000px] border-[1px] border-solid border-[#25BD66] bg-[#008735] text-[#F3F3F3] text-[16px] not-italic font-semibold"
						>
							<ContentCopyIcon fontSize="small" />
							Copy
						</button>
					</div>
				</div>}
			{/* {ownerBotsList?.length > 0 &&
				<div className="flex flex-col items-center justify-between gap-[10px] w-full bg-white p-10 rounded-lg shadow-lg">
					{ownerBotsList.map((bot) => {
						const isChecked = bot.drive_mode === 'dev' || checkedStates[bot.device_ref_id];
						return (
							<div className="flex items-center justify-between w-full bg-white p-10 rounded-lg shadow-lg" key={bot.device_ref_id}>
								<div className='flex flex-col gap-[20px]'>
									<p className="text-md font-bold">Customer Support Access / {bot.device_ref_id}</p>
									<p className='text-[#727272] text-center text-[16px] not-italic font-medium leading-[22px] tracking-[-0.32px]'>
										Only turn on to allow customer support technicians to remote access the robot. Toggle off to revoke access.
									</p>
								</div>
								<div className="flex items-center gap-10">
									<Switch
										checked={isChecked}
										onChange={(event) => handleChange(event, bot.device_ref_id)}
										inputProps={{ 'aria-label': 'controlled' }}
									/>
								</div>
							</div>
						);
					})}
				</div>
			} */}
		</section>
	);
}

export default Settings;
